import React, { useState, useEffect } from 'react';
import { 
  X, 
  Trash2, 
  AlertCircle, 
  CheckCircle, 
  Loader2, 
  Clock, 
  ChevronRight, 
  Info, 
  Mail, 
  FileText,
  Users,
  Percent,
  Calculator,
  XCircle
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { format, startOfWeek, addDays, getDay } from 'date-fns';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return format(new Date(date.setDate(diff)), 'yyyy-MM-dd');
};

const InvoiceAutomationModal = ({ isOpen, onClose, onRefresh }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [matchingInvoices, setMatchingInvoices] = useState({}); // item.id -> { subId -> invoiceId }
  const [distributionTypes, setDistributionTypes] = useState({}); // item.id -> 'halve' | 'single' | 'mcl'
  const [allocations, setAllocations] = useState({}); // item.id -> [{ name, rate, subId }]
  const [previewSlipUrl, setPreviewSlipUrl] = useState(null);

  useEffect(() => {
    if (isOpen) fetchPendingItems();
  }, [isOpen]);

  const fetchPendingItems = async () => {
    setLoading(true);
    try {
      const { data: pending, error } = await supabase
        .from('pending_invoice_items')
        .select(`
          *,
          depots (name, standby_hours_default, contacts)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Group and enhance with allocations
      const enhancedItems = [];
      const matches = {};
      const allocsMap = {};
      const defaults = {};

      for (const item of pending) {
        // 1. Fetch current allocations for this crew and day
        const dayOfWeek = getDay(new Date(item.item_date));
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        
        const { data: crews } = await supabase.from('crews').select('id').eq('crew_identifier', item.crew_identifier.toUpperCase()).maybeSingle();
        
        let crewAllocs = [];
        if (crews) {
          const { data: allocData } = await supabase
            .from('crew_allocations')
            .select(`
              subcontractor_id,
              subcontractors (
                full_name,
                first_name,
                last_name,
                payment_schedule_id,
                payment_schedules (
                  id,
                  daily_rates (day_of_week, hourly_rate)
                )
              )
            `)
            .eq('crew_id', crews.id)
            .eq('date', item.item_date);
          
          crewAllocs = (allocData || []).map(a => {
            const sub = a.subcontractors;
            const rates = sub?.payment_schedules?.daily_rates || [];
            const dayRate = rates.find(r => r.day_of_week === dayNames[dayOfWeek])?.hourly_rate || 0;
            return {
              subId: a.subcontractor_id,
              name: sub?.full_name || `${sub?.first_name} ${sub?.last_name}`,
              rate: dayRate
            };
          });
        }

        allocsMap[item.id] = crewAllocs;
        
        // Use database-provided distribution type or fallback to smart default
        let defaultType = 'single';
        if (item.distribution_type === 'split' || (crewAllocs.length > 1 && !item.distribution_type)) {
            defaultType = 'halve';
        } else if (item.distribution_type === 'individual') {
            defaultType = 'single';
        }
        
        defaults[item.id] = defaultType;
        // 2. Fetch invoice matches for these subcontractors
        const subMatches = {};
        for (const sa of crewAllocs) {
          const { data: inv } = await supabase
            .from('invoices')
            .select('id, status')
            .eq('subcontractor_id', sa.subId)
            .eq('week_commencing_date', item.week_commencing_date || getMonday(item.item_date))
            .maybeSingle();
          if (inv) subMatches[sa.subId] = inv.id;
        }

        matches[item.id] = subMatches;
        enhancedItems.push(item);
      }

      setItems(enhancedItems);
      setMatchingInvoices(matches);
      setAllocations(allocsMap);
      setDistributionTypes(defaults);

    } catch (error) {
      toast({ title: 'Fetch Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const calculatePayoutPreview = (item) => {
    const distType = distributionTypes[item.id];
    const itemAllocs = allocations[item.id] || [];
    const isStandby = item.description?.toLowerCase().includes('standby');
    
    if (isStandby && item.standby_hours) {
      const payouts = itemAllocs.map(a => ({
        name: a.name,
        amount: a.rate * item.standby_hours
      }));
      const totalPayout = payouts.reduce((sum, p) => sum + p.amount, 0);
      return {
        isStandby: true,
        payouts,
        profit: item.slip_total_net - totalPayout
      };
    }

    // Deductions/PCNs: Full amount is split between crew, no business "profit" usually
    if (item.item_type === 'deduction') {
      const fullAmount = Number(item.slip_total_net);
      if (distType === 'halve') {
        const half = fullAmount / 2;
        return {
          amountPerSub: half,
          targets: itemAllocs.length > 0 ? itemAllocs : [{ name: 'System Crew' }],
          profit: 0
        };
      } else {
        return {
          amountPerSub: fullAmount,
          targets: itemAllocs.slice(0, 1),
          profit: 0
        };
      }
    }

    // Standard Units (5 Star, Extra Drops): Always split 50/50 with the business
    const unitAmount = Number(item.slip_total_net) / 2; // Always halve the base unit
    if (distType === 'halve') {
      return {
        amountPerSub: unitAmount,
        targets: itemAllocs.length > 0 ? itemAllocs : [{ name: 'System Crew' }],
        profit: 0
      };
    } else if (distType === 'single') {
      return {
        amountPerSub: unitAmount,
        targets: itemAllocs.slice(0, 1),
        profit: unitAmount
      };
    } else { // MCL
      return {
        amountPerSub: unitAmount,
        targets: itemAllocs.slice(0, 1),
        profit: 0 // External, not profit
      };
    }
  };

  const handleApprove = async (item) => {
    setIsProcessing(true);
    try {
      const preview = calculatePayoutPreview(item);
      const subMatches = matchingInvoices[item.id] || {};
      const itemAllocs = allocations[item.id] || [];

      // 1. Validate invoice matches
      let targets = [];
      if (preview.isStandby) {
        targets = preview.payouts.map(p => {
          const subInfo = itemAllocs.find(a => a.name === p.name);
          return { subId: subInfo?.subId, name: p.name, amount: p.amount };
        });
      } else {
        targets = preview.targets.map(t => ({
          subId: t.subId,
          name: t.name,
          amount: preview.amountPerSub
        }));
      }

      for (const t of targets) {
        if (!t.subId || !subMatches[t.subId]) {
          throw new Error(`Missing draft invoice for ${t.name}. Visit Invoicing first.`);
        }
      }

      // 2. Create Adjustments
      for (const t of targets) {
        await supabase.from('invoice_adjustments').insert({
          invoice_id: subMatches[t.subId],
          description: `${item.description} (Ref: ${item.id.slice(0, 8)})`,
          type: item.item_type,
          amount: t.amount,
          adjustment_date: item.item_date
        });
      }

      // 3. Mark as Approved
      await supabase.from('pending_invoice_items').update({ 
        status: 'approved',
        metadata: {
          ...item.metadata,
          approved_preview: preview,
          distribution_used: distributionTypes[item.id],
          approved_at: new Date().toISOString()
        }
      }).eq('id', item.id);

      toast({ title: 'Approved', description: 'Adjustments applied successfully.' });
      setItems(items.filter(i => i.id !== item.id));
    } catch (error) {
      toast({ title: 'Approval Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (!confirm('Are you sure you want to REJECT ALL pending items?')) return;
    try {
      await supabase.from('pending_invoice_items').update({ status: 'rejected' }).eq('status', 'pending');
      setItems([]);
      toast({ title: 'Cleared', description: 'All items rejected.' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // Group by Crew + Week
  const groupedItems = items.reduce((acc, item) => {
    const key = `${item.crew_identifier}-${item.week_commencing_date}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 p-0 rounded-3xl shadow-2xl">
        
        {/* Header Section */}
        <div className="p-10 pb-6 border-b border-slate-200/50 dark:border-slate-800/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="p-4 rounded-3xl bg-blue-600 shadow-xl shadow-blue-500/30">
                <Calculator className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Invoice Automation Terminal</h2>
                <p className="text-slate-500 font-medium font-mono text-[10px] uppercase tracking-wider">Adjustments accounting engine</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={handleBulkReject}
                className="px-6 py-2.5 rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 font-bold text-sm hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all border border-rose-200/50"
              >
                Reject All
              </button>
              <div className="px-6 py-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-bold text-sm">
                {items.length} Pending Actions
              </div>
            </div>
          </div>
        </div>

        {/* List Section */}
        <div className="flex-1 overflow-y-auto p-10 space-y-12">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-40 gap-4">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
              <p className="text-slate-400 font-black uppercase text-xs tracking-widest">Hydrating Financial Data...</p>
            </div>
          ) : Object.keys(groupedItems).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-40 gap-6 opacity-30">
              <CheckCircle className="w-20 h-20 text-slate-300" />
              <p className="text-2xl font-black text-slate-400">All Systems Balanced</p>
            </div>
          ) : (
            Object.values(groupedItems).map((group, idx) => {
              const first = group[0];
              return (
                <div key={idx} className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
                  {/* Group Header */}
                  <div className="p-8 border-b border-slate-100 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-black text-blue-600 uppercase tracking-widest">Crew</span>
                          <span className="text-lg font-black text-slate-900 dark:text-white">{first.crew_identifier}</span>
                        </div>
                        <h4 className="font-bold text-sm text-slate-700 dark:text-slate-300 uppercase tracking-tight">
                          Week Commencing: {format(new Date(first.week_commencing_date), 'do MMM')}
                        </h4>
                        <div className="flex gap-2 mt-1">
                          {(allocations[first.id] || []).map((a, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-[10px] font-bold border border-blue-100/50">
                              {a.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">Group Total</p>
                      <p className="text-2xl font-black text-slate-900 dark:text-white">
                        £{group.reduce((sum, item) => sum + Number(item.slip_total_net || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Group Items */}
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {group.map((item) => {
                      const prev = calculatePayoutPreview(item);
                      const isMatched = Object.keys(matchingInvoices[item.id] || {}).length > 0;
                      
                      return (
                        <div key={item.id} className="p-8 hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-all flex items-start justify-between gap-10">
                          <div className="space-y-4 flex-1">
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${item.item_type === 'addition' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                {item.item_type}
                              </span>
                              <span className="text-slate-400 font-bold text-xs uppercase letter-spacing-1">
                                {format(new Date(item.item_date), 'EEE do MMM')}
                              </span>
                              {item.status === 'clarification_requested' && (
                                <span className="flex items-center gap-1 text-[10px] font-black text-rose-500 uppercase bg-rose-50 px-2 py-1 rounded-md border border-rose-200 animate-pulse">
                                  <AlertCircle size={10} /> Review Required (OTR?)
                                </span>
                              )}
                              {!isMatched && (
                                <span className="flex items-center gap-1 text-[10px] font-black text-amber-500 uppercase bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                                  <AlertCircle size={10} /> No Invoice Match
                                </span>
                              )}
                              {item.metadata?.is_otr && (
                                <span className="text-[10px] font-black text-blue-500 uppercase bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200">
                                  OTR RUN
                                </span>
                              )}
                            </div>
                            
                            <div>
                              <h5 className="text-lg font-black text-slate-800 dark:text-white leading-tight">
                                {item.description || 'Additional Payment'}
                              </h5>
                              <p className="text-slate-500 text-sm mt-1">Source: {item.attachment_name || 'Slip Attachment'}</p>
                            </div>

                            {/* Distribution Controls */}
                            {!prev.isStandby && (
                                <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl w-fit">
                                    {['halve', 'single', 'mcl'].map((type) => (
                                        <button
                                            key={type}
                                            onClick={() => setDistributionTypes(prev => ({ ...prev, [item.id]: type }))}
                                            className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${distributionTypes[item.id] === type ? 'bg-white dark:bg-slate-700 shadow-md text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Preview Badge */}
                            <div className="flex flex-wrap gap-4 mt-4">
                               {prev.isStandby ? (
                                   <>
                                      {prev.payouts.map((p, i) => (
                                          <div key={i} className="px-4 py-2 rounded-2xl bg-blue-50 border border-blue-100 flex flex-col">
                                              <span className="text-[10px] font-black text-blue-400 uppercase">Payout ({p.name})</span>
                                              <span className="font-black text-blue-700">£{p.amount.toFixed(2)}</span>
                                          </div>
                                      ))}
                                      {prev.profit > 0 && (
                                          <div className="px-4 py-2 rounded-2xl bg-emerald-50 border border-emerald-100 flex flex-col">
                                              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-tighter">Business Commision</span>
                                              <span className="font-black text-emerald-700">£{prev.profit.toFixed(2)}</span>
                                          </div>
                                      )}
                                   </>
                               ) : (
                                   <>
                                      {prev.targets.map((t, i) => (
                                          <div key={i} className="px-4 py-2 rounded-2xl bg-blue-50 border border-blue-100 flex flex-col">
                                              <span className="text-[10px] font-black text-blue-400 uppercase">{i === 0 ? 'Primary' : 'Secondary'} Payout</span>
                                              <span className="font-black text-blue-700">£{prev.amountPerSub.toFixed(2)}</span>
                                          </div>
                                      ))}
                                      {prev.profit > 0 && (
                                          <div className="px-4 py-2 rounded-2xl bg-emerald-50 border border-emerald-100 flex flex-col">
                                              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-tighter">Business P/L</span>
                                              <span className="font-black text-emerald-700">£{prev.profit.toFixed(2)}</span>
                                          </div>
                                      )}
                                   </>
                               )}
                            </div>
                          </div>

                          <div className="flex flex-col gap-3 min-w-[200px] pt-2">
                             <div className="text-right mb-4">
                               <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider leading-none">Slip Value</p>
                               <h6 className="text-3xl font-black text-slate-900 dark:text-white group-hover:scale-105 transition-transform">£{Number(item.slip_total_net).toFixed(2)}</h6>
                             </div>

                             <div className="flex gap-2">
                                <Button 
                                    onClick={() => {
                                      const p = item.metadata?.page_number;
                                      setPreviewSlipUrl(`${item.attachment_url}${p ? `#page=${p}` : ''}`);
                                    }}
                                    className="h-12 w-full rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 font-black uppercase text-[10px] tracking-widest border-none transition-all shadow-none"
                                >
                                    <FileText size={16} className="mr-2" /> Slip {item.metadata?.page_number && <span className="opacity-40">#P{item.metadata.page_number}</span>}
                                </Button>
                                <Button 
                                    className="h-12 w-full rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-black uppercase text-[10px] tracking-widest transition-all shadow-xl shadow-blue-500/20"
                                    onClick={() => handleApprove(item)}
                                    disabled={isProcessing || !isMatched}
                                >
                                    {isProcessing ? <Loader2 className="animate-spin w-4 h-4" /> : 'Apply'}
                                </Button>
                             </div>
                             
                             <button 
                                onClick={async () => {
                                    if(confirm('Reject this item?')) {
                                        await supabase.from('pending_invoice_items').update({ status: 'rejected' }).eq('id', item.id);
                                        setItems(prev => prev.filter(i => i.id !== item.id));
                                    }
                                }}
                                className="text-[10px] font-black text-rose-500 hover:text-rose-700 uppercase tracking-[0.2em] pt-2 text-center"
                             >
                                Reject Line
                             </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Action Bar */}
        <div className="p-10 border-t border-slate-200/50 dark:border-slate-800/50 bg-white dark:bg-slate-900 flex justify-end">
            <Button 
              onClick={onClose}
              className="px-12 h-14 rounded-3xl bg-slate-900 text-white font-black uppercase text-sm tracking-widest hover:scale-105 transition-all shadow-2xl"
            >
              Close Terminal
            </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Standalone Slip Preview Overlay - Placed outside the Dialog for isolation */}
    <Dialog open={!!previewSlipUrl} onOpenChange={(open) => { if (!open) setPreviewSlipUrl(null); }}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0 rounded-[2rem] overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
        <div className="p-6 flex justify-between items-center border-b border-slate-100 dark:border-slate-800 shrink-0">
          <h3 className="font-black text-xl text-slate-900 dark:text-white tracking-tight uppercase">Document Verification</h3>
          <button
            onClick={() => setPreviewSlipUrl(null)}
            className="p-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl transition-all"
          >
            <XCircle className="w-6 h-6 text-slate-400 hover:text-slate-600" />
          </button>
        </div>
        <div className="flex-1 bg-slate-100 dark:bg-black min-h-0">
          <iframe
            src={previewSlipUrl}
            className="w-full h-full border-none"
            title="Slip Viewer"
          />
        </div>
        <div className="p-6 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end shrink-0">
          <Button
            onClick={() => setPreviewSlipUrl(null)}
            className="px-10 h-12 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-black uppercase text-xs tracking-widest hover:scale-105 transition-all shadow-xl shadow-slate-900/20"
          >
            Return to Terminal
          </Button>
        </div>
      </DialogContent>
    </Dialog>

</>
  );
};

export default InvoiceAutomationModal;
